import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { FiLoader, FiX, FiActivity, FiInfo } from 'react-icons/fi';
import { BiTable } from 'react-icons/bi';
import QueryResultContent from './QueryResultContent';
import InteractivePlanContent from './InteractivePlanContent';
import PlanCompare from './PlanCompare';
import styles from './MainDisplayTabs.module.css';
import { lastExecButtons } from '../utils/execMonitor';


const MainDisplayTabs = ({
    activeMainTab,
    setActiveMainTab,
    queryResultTabs,
    activeQueryResultTabId,
    setActiveQueryResultTabId,
    onCloseQueryResultTab,
    onPinQueryResultTab,    // 쿼리 결과 탭 고정 함수
    tableInfoTabs,          // 새 prop 추가
    activeTableInfoTabId,   // 새 prop 추가
    setActiveTableInfoTabId,// 새 prop 추가
    onCloseTableInfoTab,    // 새 prop 추가
    executionPlanTabs,
    activeExecutionPlanTabId,
    setActiveExecutionPlanTabId,
    onCloseExecutionPlanTab, // 실행계획 탭 닫기 함수 추가
    onFetchMoreData,
    onFetchAll,             // 전체출력 함수
    onExportCSV,            // CSV 저장 함수
    onStopQuery,            // 쿼리 중지 함수
    onMonitorQuery,         // 실행 중인 문장의 모니터링 창 열기(세션에 묶인 결과 탭만)
    onActualPlan,           // 방금 실행한 문장의 실측 실행계획(DISPLAY_CURSOR) — 다시 실행하지 않는다
    onMonitorReport,        // 방금 실행한 문장의 SQL Monitor 리포트
    sessionsList,           // 세션 목록(폴링) — 위 두 단추의 상태(Oracle 인가 · STATISTICS_LEVEL)
    columnComments = {},    // 컬럼 주석 매핑
    onPlanNodeClick,        // 실행계획 노드 클릭 핸들러
    onShowTableDDL,         // 테이블 전체 DDL 을 오브젝트 정보 탭으로
    statsConnectionId,      // 오브젝트 통계를 조회할 연결 (이 계획을 만든 그 연결)
    activePlanNodeId = null,// 활성화된 실행계획 노드 ID
    isResultSplit = false,  // 수직분할 상태
    isCompactGrid = false,  // 그리드 압축 보기 (cell padding 최소화)
    showUserComments = false, // 결과 표에 사용자 주석 열을 붙일지(결과 도구 줄의 체크)
    showPlanObjectDetail = false, // 실행계획 탭 오른쪽의 「오브젝트 상세」를 펼칠지 (기본 꺼짐)
    onRequestObjectDetail,  // 계획의 객체를 눌렀는데 상세가 접혀 있으면 편다
    onSavePlanToArchive,    // 계획을 보관함에 저장 (App 이 제목을 묻고 서버로 보낸다)
    onPlanToReview,         // 이 계획으로 SQL 진단 리포트 만들기 (App 이 창을 연다)
    onPlanMonitorReport,    // 이 계획의 SQL Monitor 리포트 (App.handlePlanMonitorReport)
    planOptions,            // 실행계획 옵션(「실행」 설정) — 예상/실제·형식·실측 방식·SQL Monitor 리포트(2026-10-26)
    onPlanOptionsChange,
    onRerunPlan,            // 이 탭의 SQL 을 지금 옵션으로 다시 뜬다
    onAiTunePlan,           // 이 SQL 로 SQL 튜닝 작업 → 「AI 튜닝 자동 진행」(AI 빌드만)
    onExportPlanFile,       // 계획을 .md/.livesqlplan 파일로 내려받기
    gridFormat,             // 결과 그리드 표시 포맷 (NULL 표기·날짜·천단위)
    onPasteToEditor,        // 셀/헤더 더블클릭 시 에디터 cursor 위치에 텍스트 삽입
    tabKindFilter = { result: true, plan: true, tableInfo: true }, // 헤더에 표시할 탭 종류 필터
}) => {
    const { t } = useTranslation();

    const [activeTableInfoSubTab, setActiveTableInfoSubTab] = useState('columns');

    // 전체출력 핸들러
    const handleFetchAll = (tab) => {
        if (onFetchAll && tab.hasMore && !tab.isFetchingAll) {
            onFetchAll(tab.id);
        }
    };

    // CSV 저장 핸들러
    const handleExportCSV = (tab) => {
        if (onExportCSV && tab.data && tab.data.length > 0) {
            onExportCSV(tab.id);
        }
    };

    // body 만 렌더 — 헤더는 통합 헤더에서 처리.
    const renderQueryResultsBody = () => {
        const activeTab = queryResultTabs.find(tab => tab.id === activeQueryResultTabId);
        return (
            <div className={styles.queryResultTabContainer}>
                {(!queryResultTabs || queryResultTabs.length === 0) ? (
                    <div className={styles.noTabsMessage}>{t('common:mdt.noQueryResult')}</div>
                ) : activeTab ? (
                    <QueryResultContent activeTab={activeTab} onFetchMoreData={onFetchMoreData} columnComments={columnComments} isCompactGrid={isCompactGrid} showUserComments={showUserComments} gridFormat={gridFormat} onPasteToEditor={onPasteToEditor} />
                ) : (
                    <div className={styles.noTabsMessage}>{t('common:mdt.noResultTab')}</div>
                )}
            </div>
        );
    };

    const renderExecutionPlanBody = () => {
        if (!executionPlanTabs || executionPlanTabs.length === 0) {
            return <div className={styles.noTabsMessage}>{t('common:mdt.noPlan')}</div>;
        }
        const activeTab = executionPlanTabs.find(tab => tab.id === activeExecutionPlanTabId);
        // 개선 전/후 비교 탭은 계획 둘을 나란히 그린다 — 모양이 다르므로 갈라 준다
        if (activeTab && activeTab.compare) {
            return (
                <div className={styles.subTabsContainer}>
                    <PlanCompare
                        tab={activeTab}
                        statsConnectionId={statsConnectionId}
                        onPlanNodeClick={onPlanNodeClick}
                        onShowTableDDL={onShowTableDDL}
                        showPlanObjectDetail={showPlanObjectDetail}
                        onSaveToArchive={onSavePlanToArchive}
                    />
                </div>
            );
        }
        return (
            <div className={styles.subTabsContainer}>
                <InteractivePlanContent
                    activeTab={activeTab}
                    onPlanNodeClick={onPlanNodeClick}
                    statsConnectionId={statsConnectionId}
                    onShowTableDDL={onShowTableDDL}
                    activePlanNodeId={activePlanNodeId}
                    isResultSplit={isResultSplit}
                    showPlanObjectDetail={showPlanObjectDetail}
                    onRequestObjectDetail={onRequestObjectDetail}
                    onSavePlanToArchive={onSavePlanToArchive}
                    onPlanToReview={onPlanToReview}
                    onPlanMonitorReport={onPlanMonitorReport}
                    onExportPlanFile={onExportPlanFile}
                    planOptions={planOptions}
                    onPlanOptionsChange={onPlanOptionsChange}
                    onRerunPlan={onRerunPlan}
                    onAiTunePlan={onAiTunePlan}
                />
            </div>
        );
    };

    const renderTableInfoBody = () => {
        if (!tableInfoTabs || tableInfoTabs.length === 0) {
            return <div className={styles.noTabsMessage}>{t('common:mdt.pickInExplorer')}</div>;
        }
        const activeTab = tableInfoTabs.find(tab => tab.id === activeTableInfoTabId);
        return (
            <div className={styles.queryResultTabContainer}>
                {!activeTab ? (
                    <div className={styles.noTabsMessage}>{t('common:mdt.noObjectTab')}</div>
                ) : activeTab.isLoading ? (
                    <div className={styles.tabContentLoading}>{t('common:mdt.loadingObjectFor', { table: activeTab.identifier.tableName })}</div>
                ) : activeTab.error ? (
                    <div className={styles.tabContentError}>{t('common:mdt.errorFor', { message: activeTab.error, table: activeTab.identifier.tableName })}</div>
                ) : activeTab.data?.ddl !== undefined ? (
                    /* DDL 스크립트 표시 (뷰/프로시저) */
                    <div className={styles.subTabsContainer}>
                        <div className={styles.subTabsHeader}>
                            <button className={`${styles.subTabButton} ${styles.active}`}>
                                {activeTab.data.type || 'DDL'} {t('common:mdt.definition')}
                            </button>
                        </div>
                        <div className={styles.subTabsContent} style={{ overflow: 'auto', flex: 1 }}>
                            {/* 카탈로그로 조립한 것이면 **그렇다고 말한다** — 원본과 다른데 같은 줄 알면 곤란하다 */}
                            {activeTab.data.reconstructed && (
                                <div className={styles.ddlNote}>{t('common:mdt.ddlReconstructed')}</div>
                            )}
                            <pre className={styles.planTextOutput}>{activeTab.data.ddl}</pre>
                        </div>
                    </div>
                ) : (
                    <div className={styles.subTabsContainer}>
                        {/* 컬럼·인덱스 탭은 나란히(2026-10-28 신고) — 양 끝 정렬이라 「인덱스 목록」 이 오른쪽 끝으로 밀려 탭처럼 보이지 않았다. 건수를 함께 보인다 */}
                        <div className={`${styles.subTabsHeader} ${styles.infoSubTabs}`}>
                            <button
                                className={`${styles.subTabButton} ${activeTableInfoSubTab === 'columns' ? styles.active : ''}`}
                                onClick={() => setActiveTableInfoSubTab('columns')}
                            >
                                {t('common:mdt.columnList')}{activeTab.data && Array.isArray(activeTab.data.columns) ? ` (${activeTab.data.columns.length})` : ''}
                            </button>
                            <button
                                className={`${styles.subTabButton} ${activeTableInfoSubTab === 'indexes' ? styles.active : ''}`}
                                onClick={() => setActiveTableInfoSubTab('indexes')}
                            >
                                {t('common:mdt.indexList')}{activeTab.data && Array.isArray(activeTab.data.indexes) ? ` (${activeTab.data.indexes.length})` : ''}
                            </button>
                        </div>
                        <div className={styles.subTabsContent}>
                            {activeTableInfoSubTab === 'columns' && (
                                <div className={styles.tableDetailContent}> 
                                    <h4>{t('common:mdt.columnInfoFor', { target: `${activeTab.identifier.schemaName}.${activeTab.identifier.tableName}` })}</h4>
                                    {activeTab.data && activeTab.data.columns && activeTab.data.columns.length > 0 ? (
                                        <table className={`${styles.infoTable} ${styles.infoTableCompact}`}>
                                            <thead>
                                                <tr>
                                                    <th>{t('common:mdt.columnId')}</th>
                                                    <th>{t('common:mdt.columnName')}</th>
                                                    <th>{t('common:mdt.type')}</th>
                                                    <th>{t('common:mdt.length')}</th>
                                                    <th>{t('common:mdt.nullable')}</th>
                                                    <th>{t('common:mdt.comment')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {activeTab.data.columns.map((col, index) => (
                                                    <tr key={index}>
                                                        <td>{col.columnid}</td>
                                                        <td>{col.name}</td>
                                                        <td>{col.type}</td>
                                                        <td>{col.length}</td>
                                                        <td>{col.nullable}</td>
                                                        <td>{col.comments}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : <p>{t('common:mdt.noColumnInfo', { table: activeTab.identifier.tableName })}</p>}
                                </div>
                            )}
                            {activeTableInfoSubTab === 'indexes' && (
                                <div className={styles.tableDetailContent}>
                                    <h4>{t('common:mdt.indexInfoFor', { target: `${activeTab.identifier.schemaName}.${activeTab.identifier.tableName}` })}</h4>
                                    {activeTab.data && activeTab.data.indexes && activeTab.data.indexes.length > 0 ? (
                                        <table className={`${styles.infoTable} ${styles.infoTableCompact}`}>
                                            <thead>
                                                <tr>
                                                    <th>{t('common:mdt.indexName')}</th>
                                                    <th>{t('common:mdt.column')}</th>
                                                    <th>{t('common:mdt.unique')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {activeTab.data.indexes.map((idx, index) => (
                                                    <tr key={index}>
                                                        <td>{idx.name}</td>
                                                        <td>{Array.isArray(idx.columns) ? idx.columns.join(', ') : ''}</td>
                                                        <td>{idx.isUnique ? 'Unique' : 'Non-Unique'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                     ) : <p>{t('common:mdt.noIndexInfo', { table: activeTab.identifier.tableName })}</p>}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderActiveBody = () => {
        switch (activeMainTab) {
            case 'queryResults':   return renderQueryResultsBody();
            case 'executionPlans': return renderExecutionPlanBody();
            case 'tableInfo':      return renderTableInfoBody();
            default:               return <div className={styles.tabContent}>{t('common:mdt.noSelectedTab')}</div>;
        }
    };

    // 통합 탭 리스트 — 결과/실행계획/오브젝트정보 합쳐서 id 안의 timestamp 로 정렬.
    // 각 탭에 kind 부여해서 아이콘 + 활성 시 setActive*Id 라우팅.
    const allTabs = useMemo(() => {
        const list = [];
        for (const t of (queryResultTabs || [])) list.push({ ...t, _kind: 'result' });
        for (const t of (executionPlanTabs || [])) list.push({ ...t, _kind: 'plan' });
        for (const t of (tableInfoTabs || [])) list.push({ ...t, _kind: 'tableInfo' });
        const tsOf = (id) => {
            const m = String(id || '').match(/_(\d{10,})/);
            return m ? Number(m[1]) : 0;
        };
        return list.sort((a, b) => tsOf(a.id) - tsOf(b.id));
    }, [queryResultTabs, executionPlanTabs, tableInfoTabs]);

    const visibleTabs = allTabs.filter(t =>
        (t._kind === 'result'    && tabKindFilter.result) ||
        (t._kind === 'plan'      && tabKindFilter.plan) ||
        (t._kind === 'tableInfo' && tabKindFilter.tableInfo)
    );

    const isTabActive = (tab) => (
        (tab._kind === 'result'    && tab.id === activeQueryResultTabId   && activeMainTab === 'queryResults') ||
        (tab._kind === 'plan'      && tab.id === activeExecutionPlanTabId && activeMainTab === 'executionPlans') ||
        (tab._kind === 'tableInfo' && tab.id === activeTableInfoTabId     && activeMainTab === 'tableInfo')
    );

    const activateTab = (tab) => {
        if (tab._kind === 'result') {
            setActiveMainTab('queryResults');
            setActiveQueryResultTabId(tab.id);
        } else if (tab._kind === 'plan') {
            setActiveMainTab('executionPlans');
            setActiveExecutionPlanTabId(tab.id);
        } else if (tab._kind === 'tableInfo') {
            setActiveMainTab('tableInfo');
            setActiveTableInfoTabId(tab.id);
        }
    };

    const closeTab = (tab) => {
        if (tab._kind === 'result')         onCloseQueryResultTab(tab.id);
        else if (tab._kind === 'plan')      onCloseExecutionPlanTab(tab.id);
        else if (tab._kind === 'tableInfo') onCloseTableInfoTab(tab.id);
    };

    const tabKindIcon = (kind) => {
        if (kind === 'plan')      return <FiActivity size={11} title={t('common:mdt.plan')} />;
        if (kind === 'tableInfo') return <FiInfo size={11} title={t('common:mdt.objectInfo')} />;
        // 제목을 아이콘에 달지 않는다 — 결과 탭은 이 아이콘을 감싼 고정 단추가 「고정/해제」 를 말한다(아이콘 제목이 그것을 가린다)
        return <BiTable size={12} />;
    };

    const tabLabel = (tab) => {
        if (tab._kind === 'tableInfo') {
            // 스키마는 작게, 테이블 이름이 먼저 보이게 — 폭은 CSS 가 막고 전체 이름은 툴팁(2026-10-29 요청)
            const full = tab.name || `${tab.identifier?.schemaName}.${tab.identifier?.tableName}`;
            const dot = full.indexOf('.');
            if (dot <= 0) return <span className={styles.tabNameObject}><span className={styles.tabNameTable}>{full}</span></span>;
            return (
                <span className={styles.tabNameObject}>
                    <span className={styles.tabNameSchema}>{full.slice(0, dot)}.</span>
                    <span className={styles.tabNameTable}>{full.slice(dot + 1)}</span>
                </span>
            );
        }
        return tab.name;
    };

    // 툴팁은 **짧게**. 예전에는 실행 SQL 전문을 넣어서, 긴 쿼리면 툴팁이 화면을 덮었다.
    // SQL 은 편집기에 이미 있다 — 여기서 알고 싶은 것은 "이 탭이 언제 것이고 얼마나 걸렸나" 다.
    const tabTooltip = (tab) => {
        if (tab._kind === 'tableInfo') return `${tab.identifier?.schemaName}.${tab.identifier?.tableName}`;
        if (tab._kind !== 'result') return tab.name;

        const parts = [tab.name];
        if (tab.isExecuting) parts.push(t('common:mdt.tipRunning'));
        else if (tab.executionTime != null) parts.push(t('common:mdt.tipElapsed', { ms: Number(tab.executionTime).toFixed(1) }));
        if (Array.isArray(tab.data) && tab.data.length > 0) parts.push(t('common:mdt.tipRows', { n: tab.data.length }));
        return parts.join(' · ');
    };

    // 활성 탭이 결과탭일 때만 actions area (전체출력/CSV) 표시.
    const activeQueryResult = (activeMainTab === 'queryResults')
        ? queryResultTabs.find(tab => tab.id === activeQueryResultTabId)
        : null;
    // 「실행계획(실제)」 · 「SQL Monitor 리포트」 — 세션에 묶인 Oracle 결과 탭에만 선다(utils/execMonitor.lastExecButtons)
    const lastExec = activeQueryResult ? lastExecButtons(activeQueryResult, sessionsList) : null;


    return (
        <div className={styles.mainDisplayTabsContainer}>
            <div className={styles.mainTabsContentArea}>
                <div className={styles.subTabsHeader}>
                    <div className={styles.tabButtonsArea}>
                        {visibleTabs.length === 0 && (
                            <div className={styles.subTabButtonPlaceholder}>{t('common:mdt.noTabs')}</div>
                        )}
                        {visibleTabs.map(tab => {
                            const active = isTabActive(tab);
                            return (
                                <div key={`${tab._kind}:${tab.id}`} className={`${styles.subTabButtonContainer} ${active ? styles.active : ''}`}>
                                    {/* 결과 탭은 종류 아이콘이 곧 고정 단추다(2026-10-21 사용자 요청 — 따로 있던 책갈피 단추를 없앴다). 고정되면 주황색 */}
                                    {tab._kind === 'result' ? (
                                        <button
                                            type="button"
                                            className={`${styles.subTabKindIcon} ${styles.kindPinButton} ${tab.isPinned ? styles.pinned : ''}`}
                                            onClick={(e) => { e.stopPropagation(); onPinQueryResultTab(tab.id); }}
                                            aria-pressed={!!tab.isPinned}
                                            // 이름도 직접 준다 — 전역 툴팁이 title 을 옮기며 단 aria-label 은 처음 값에 머물러 고정 뒤에도 「탭 고정」 이라 읽혔다
                                            aria-label={`${t('common:mdt.queryResult')} — ${tab.isPinned ? t('common:mdt.unpinTab') : t('common:mdt.pinTab')}`}
                                            title={`${t('common:mdt.queryResult')} — ${tab.isPinned ? t('common:mdt.unpinTab') : t('common:mdt.pinTab')}`}
                                        >
                                            {tabKindIcon(tab._kind)}
                                        </button>
                                    ) : (
                                        <span className={styles.subTabKindIcon} title={
                                            tab._kind === 'plan' ? t('common:mdt.plan') : t('common:mdt.objectInfo')
                                        }>
                                            {tabKindIcon(tab._kind)}
                                        </span>
                                    )}
                                    <button
                                        className={styles.subTabButton}
                                        onClick={() => activateTab(tab)}
                                        title={tabTooltip(tab)}
                                    >
                                        {tab._kind === 'result' && tab.isExecuting && (
                                            <FiLoader size={11} className={styles.subTabSpinner} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                        )}
                                        {tabLabel(tab)}
                                    </button>
                                    {/* 실행뿐 아니라 **페이징·전체 조회 중에도** 멈출 수 있어야 한다 —
                                        셋 다 시간 제한이 없으므로, 중지가 없으면 화면이 영영 묶인다 */}
                                    {tab._kind === 'result' && (tab.isExecuting || tab.isLoadingMore || tab.isFetchingAll) && (
                                        <button
                                            className={styles.stopTabButton}
                                            onClick={(e) => { e.stopPropagation(); onStopQuery(tab.id); }}
                                            title={t('common:mdt.stopQuery')}
                                        >
                                            {t('common:mdt.stop')}
                                        </button>
                                    )}
                                    {/* 오래 걸리는 문장이 계획의 어느 줄에 있는지 본다 — 실행 중이고 세션에 묶인 탭에만(세션이 있어야 그 문장을 집는다) */}
                                    {tab._kind === 'result' && tab.isExecuting && tab.sessionId && onMonitorQuery && (
                                        <button
                                            className={styles.monitorTabButton}
                                            onClick={(e) => { e.stopPropagation(); onMonitorQuery(tab.id); }}
                                            title={t('common:mdt.monitorQuery')}
                                        >
                                            {t('common:mdt.monitor')}
                                        </button>
                                    )}
                                    <button
                                        className={styles.closeTabButton}
                                        onClick={(e) => { e.stopPropagation(); closeTab(tab); }}
                                        title="Close Tab"
                                    >
                                        <FiX size={12} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    <div className={styles.tabActionsArea}>
                        {activeQueryResult && (
                            <>
                                <button
                                    className={styles.actionButton}
                                    onClick={() => handleFetchAll(activeQueryResult)}
                                    disabled={activeQueryResult.isLoading || activeQueryResult.isFetchingAll}
                                    title={t('common:mdt.fetchAllHint')}
                                >
                                    {activeQueryResult.isFetchingAll ? t('common:mdt.fetching') : t('common:mdt.fetchAll')}
                                </button>
                                <button
                                    className={styles.actionButton}
                                    onClick={() => handleExportCSV(activeQueryResult)}
                                    disabled={!activeQueryResult.data || activeQueryResult.data.length === 0}
                                    title={t('common:mdt.saveCsvHint')}
                                >
                                    {t('common:mdt.saveCsv')}
                                </button>
                                {lastExec && onActualPlan && (
                                    <button
                                        className={styles.actionButton}
                                        onClick={() => onActualPlan(activeQueryResult.id)}
                                        disabled={!lastExec.actual.enabled}
                                        title={t(`common:lastExec.actualHint.${lastExec.actual.reason}`)}
                                    >
                                        {t('common:lastExec.actual')}
                                    </button>
                                )}
                                {lastExec && onMonitorReport && (
                                    <button
                                        className={styles.actionButton}
                                        onClick={() => onMonitorReport(activeQueryResult.id)}
                                        disabled={!lastExec.report.enabled}
                                        title={t('common:lastExec.reportHint')}
                                    >
                                        {t('common:lastExec.report')}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
                {renderActiveBody()}
            </div>
        </div>
    );
};

MainDisplayTabs.propTypes = {
    activeMainTab: PropTypes.string.isRequired,
    setActiveMainTab: PropTypes.func.isRequired,
    queryResultTabs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        data: PropTypes.any.isRequired,
        query: PropTypes.string,
        cursorId: PropTypes.string,
        hasMore: PropTypes.bool,
        isLoadingMore: PropTypes.bool,
        error: PropTypes.string,
        isPinned: PropTypes.bool, // 탭 고정 상태
    })),
    activeQueryResultTabId: PropTypes.string,
    setActiveQueryResultTabId: PropTypes.func,
    onCloseQueryResultTab: PropTypes.func,
    onPinQueryResultTab: PropTypes.func, // 탭 고정 함수
    tableInfoTabs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        identifier: PropTypes.shape({
            connectionId: PropTypes.string,
            schemaName: PropTypes.string,
            tableName: PropTypes.string,
            dbType: PropTypes.string,
        }).isRequired,
        data: PropTypes.shape({
            columns: PropTypes.arrayOf(PropTypes.object),
            indexes: PropTypes.arrayOf(PropTypes.object),
        }),
        isLoading: PropTypes.bool,
        error: PropTypes.string,
    })),
    activeTableInfoTabId: PropTypes.string,
    setActiveTableInfoTabId: PropTypes.func,
    onCloseTableInfoTab: PropTypes.func,
    executionPlanTabs: PropTypes.arrayOf(PropTypes.shape({ 
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        data: PropTypes.string, 
        query: PropTypes.string,
    })),
    activeExecutionPlanTabId: PropTypes.string,
    setActiveExecutionPlanTabId: PropTypes.func,
    onCloseExecutionPlanTab: PropTypes.func, // 실행계획 탭 닫기 함수 PropType 추가
    onFetchMoreData: PropTypes.func,
    onFetchAll: PropTypes.func,        // 전체출력 함수
    onExportCSV: PropTypes.func,       // CSV 저장 함수
    onStopQuery: PropTypes.func,       // 쿼리 중지 함수
    onMonitorQuery: PropTypes.func,
    onActualPlan: PropTypes.func,
    onMonitorReport: PropTypes.func,
    sessionsList: PropTypes.array,
};


export default MainDisplayTabs;
